import { Box, Button, styled } from '@mui/material';
import { Row } from 'components/Layout';
import DataRoomGeneral from './General';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DataRoomFormData, defaultData, schema } from './formData';
import { useEffect } from 'react';
import { useDataRoomRequests } from './useDataRoomRequests';
import { mapDataRoomResponseToFormData } from './mapping';
import { dataRoomTabs, DataRoomTab } from './tabs';
import { match } from 'ts-pattern';
import VerticalTabs from 'components/new/VerticalTabs';
import { useVerticalTabs } from 'components/new/VerticalTabs/useVerticalTabs';

type Props = {
  dataRoomId: string;
};

const SaveContainer = styled(Box)(({ theme }) => ({
  borderTopStyle: 'solid',
  borderTopWidth: '1px',
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  bottom: 0,
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.lg),
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(theme.spacingSizes.lg, 0),
  maxWidth: '600px',
}));

const Wrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
}));

const DataRoomEdit: React.FC<Props> = ({ dataRoomId }) => {
  const { data, update } = useDataRoomRequests(dataRoomId);
  const { selectedTab, setSelectedTab } =
    useVerticalTabs<DataRoomTab>('general');

  const { control, formState, reset, handleSubmit } = useForm<DataRoomFormData>(
    {
      shouldFocusError: true,
      resolver: zodResolver(schema),
      values: defaultData,
      criteriaMode: 'all',
    },
  );

  const handleSave = (data: DataRoomFormData) => {
    update({
      id: dataRoomId,
      headline: data.headline,
      subheading: data.subheading,
      contact: {
        email: data.contact.email,
        name: data.contact.name,
        phone: data.contact.phone,
        image: data.contact.image?.objectKey ?? '',
      },
      keyInformation: data.keyInformation,
    });
  };

  useEffect(() => {
    if (!data) return;
    const newFormData = mapDataRoomResponseToFormData(data);
    reset(newFormData);
  }, [data, reset]);

  return (
    <>
      <Wrapper>
        <VerticalTabs
          tabs={dataRoomTabs}
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          menuProps={{ sx: { width: 244 } }}
          renderContent={(tab) => (
            <ContentContainer>
              {match(tab)
                .with('general', () => <DataRoomGeneral control={control} />)
                .otherwise(() => (
                  <></>
                ))}
            </ContentContainer>
          )}
        />
      </Wrapper>
      {formState.isDirty && (
        <SaveContainer>
          <Row justify="flex-end">
            <Button
              onClick={handleSubmit(handleSave)}
              variant="contained"
              size="large"
            >
              Save
            </Button>
          </Row>
        </SaveContainer>
      )}
    </>
  );
};

export default DataRoomEdit;
